import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Paid, UploadFile } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import StandardPagination from "../components/Pagination/StandardPagination";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import ServiceTypeModal from "../components/modals/ServiceTypeModal";
import ImportSheetModal from "../components/modals/ImportSheetModal";
import { entityProfessionalTypeService } from "../services/entityProfessionalTypeService";
import {
  serviceTypeService,
  ServiceTypeItem,
  ServiceOrigin,
  SERVICE_ORIGIN_LABELS,
} from "../services/serviceTypeService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";

interface ProfOption {
  id: string;
  name: string;
}

const EntityServiceTypes: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";
  const canServiceValues = (user?.allowedPages || []).includes(
    "service-professional-values"
  );

  const [items, setItems] = useState<ServiceTypeItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [profFilter, setProfFilter] = useState("");
  const [originFilter, setOriginFilter] = useState<ServiceOrigin | "">("");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">(
    "active"
  );
  const [professionalTypes, setProfessionalTypes] = useState<ProfOption[]>([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceTypeItem | null>(null);
  const [toDelete, setToDelete] = useState<ServiceTypeItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const profNameById = useMemo(() => {
    const map = new Map<string, string>();
    professionalTypes.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [professionalTypes]);

  // Ordena por tipo de profissional e, dentro dele, por descrição (pedido do Hilton).
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const profA = (a.professionalType && profNameById.get(a.professionalType)) || "";
      const profB = (b.professionalType && profNameById.get(b.professionalType)) || "";
      const byProf = profA.localeCompare(profB, "pt-BR");
      if (byProf !== 0) return byProf;
      const byDescription = (a.description || "").localeCompare(b.description || "", "pt-BR");
      if (byDescription !== 0) return byDescription;
      // Mesmo procedimento em vários planos: o plano desempata, senão a ordem muda a cada consulta.
      return (a.healthPlanName || "").localeCompare(b.healthPlanName || "", "pt-BR");
    });
  }, [items, profNameById]);

  const loadProfessionalTypes = async () => {
    if (!entityId) return;
    try {
      const data = await entityProfessionalTypeService.getByEntity(entityId);
      setProfessionalTypes(
        data.linked.map((p) => ({ id: p.id, name: p.name }))
      );
    } catch (error) {
      console.error("Erro ao carregar tipos profissionais:", error);
    }
  };

  const loadServiceTypes = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const data = await serviceTypeService.getByEntity({
        entityId,
        professionalType: profFilter || undefined,
        origin: originFilter || undefined,
        active: statusFilter === "all" ? undefined : statusFilter === "active",
        pageNumber: currentPage,
        pageSize: itemsPerPage,
      });
      setItems(data.items);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Erro ao carregar serviços:", error);
      showToast("Erro ao carregar os serviços.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${entityName} / Tipos de Serviço`;
    loadProfessionalTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadServiceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, profFilter, originFilter, statusFilter]);


  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (item: ServiceTypeItem) => {
    setEditing(item);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      await serviceTypeService.remove(toDelete.id);
      showToast("Serviço excluído com sucesso.", "success");
      setToDelete(null);
      // Se a página esvaziou, recua uma página.
      if (items.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        loadServiceTypes();
      }
    } catch (error) {
      console.error("Erro ao excluir serviço:", error);
      showToast("Erro ao excluir o serviço.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaved = () => {
    showToast("Serviço salvo com sucesso.", "success");
    loadServiceTypes();
  };

  const handleToggleStatus = async (item: ServiceTypeItem) => {
    try {
      await serviceTypeService.update(item.id, { active: !item.isActive });
      loadServiceTypes();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      showToast("Erro ao alterar o status.", "error");
    }
  };

  const handleFilterChange = (value: string) => {
    setProfFilter(value);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Tipos de Serviço</h1>
              <p className="c4u-page-header__subtitle">Serviços oferecidos pela entidade, organizados por tipo de profissional.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          {/* Filtros: profissional e status */}
          <div className="c4u-panel">
          <div className="c4u-filters__row">
            <div className="c4u-filters__fields">
            <TextField
              select
              size="small"
              label="Filtrar por profissional"
              value={profFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todos</MenuItem>
              {professionalTypes.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Origem"
              value={originFilter}
              onChange={(e) => {
                setOriginFilter(e.target.value as ServiceOrigin | "");
                setCurrentPage(1);
              }}
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todas</MenuItem>
              {(Object.keys(SERVICE_ORIGIN_LABELS) as ServiceOrigin[]).map((o) => (
                <MenuItem key={o} value={o}>
                  {SERVICE_ORIGIN_LABELS[o]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as "active" | "inactive" | "all");
                setCurrentPage(1);
              }}
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
              <MenuItem value="all">Todos</MenuItem>
            </TextField>
            </div>
            <div className="c4u-filters__actions">
              <ClearFiltersButton
                disabled={
                  profFilter === "" && originFilter === "" && statusFilter === "active"
                }
                onClick={() => {
                  setProfFilter("");
                  setOriginFilter("");
                  setStatusFilter("active");
                  setCurrentPage(1);
                }}
              />
              <Tooltip title="Importar procedimentos do convênio (planilha)">
                <span>
                  <IconButton
                    className="c4u-btn-icon c4u-btn-icon--outline"
                    onClick={() => setImportOpen(true)}
                  >
                    <UploadFile />
                  </IconButton>
                </span>
              </Tooltip>
              <AddButton onClick={openCreate} title="Adicionar serviço" />
            </div>
          </div>

          <ResultCount shown={totalCount} />

          <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box className="c4u-table__header">
                <Box
                  className="admin-plans-header-cell c4u-col--md"
                  sx={{ flex: "0 0 200px", textAlign: "left" }}
                >
                  Tipo profissional
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "1 1 auto", minWidth: 160, textAlign: "left" }}
                >
                  Descrição
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--xl"
                  sx={{ flex: "0 0 110px", textAlign: "left" }}
                >
                  Origem
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--xl"
                  sx={{ flex: "0 0 110px", textAlign: "left" }}
                >
                  Qtd. pacote
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--md"
                  sx={{ flex: "0 0 120px", textAlign: "left" }}
                >
                  Atendimento
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--lg"
                  sx={{ flex: "0 0 130px", textAlign: "left" }}
                >
                  Atendimento
                </Box>
                <Box
                  className="admin-plans-header-cell c4u-col--lg"
                  sx={{ flex: "0 0 130px", textAlign: "left" }}
                >
                  Visível agenda
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 100px", textAlign: "left" }}
                >
                  Status
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 6,
                    }}
                  >
                    <CircularProgress sx={{ color: colors.primary }} />
                  </Box>
                ) : items.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: colors.textSecondary,
                    }}
                  >
                    Nenhum serviço cadastrado para este filtro.
                  </Box>
                ) : (
                  sortedItems.map((item) => (
                    <Box
                      key={item.id}
                      className="c4u-table__row"
                    >
                      <Box
                        className="admin-plans-cell c4u-col--md"
                        sx={{
                          flex: "0 0 200px",
                          textAlign: "left",
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                        data-label="Tipo profissional"
                      >
                        {(item.professionalType &&
                          profNameById.get(item.professionalType)) ||
                          "-"}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{
                          flex: "1 1 auto",
                          minWidth: 160,
                          textAlign: "left",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                        data-label="Descrição"
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <span>{item.description || "-"}</span>
                          {/* Plano e código junto ao nome: no cartão do celular a coluna Origem
                              não aparece, e é aqui que o serviço de convênio se identifica. */}
                          {item.origin === "operator" && (
                            <span
                              style={{
                                fontSize: "0.78rem",
                                color: colors.textSecondary,
                              }}
                            >
                              {[
                                item.healthPlanName,
                                item.procedureCode
                                  ? `${item.tableCode === "22" ? "TUSS" : "Tabela própria"} ${item.procedureCode}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          )}
                        </Box>
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{ flex: "0 0 110px", textAlign: "left" }}
                        data-label="Origem"
                      >
                        {item.origin === "operator" ? (
                          <Chip
                            size="small"
                            label={SERVICE_ORIGIN_LABELS.operator}
                            sx={{ bgcolor: "#cff4fc", color: "#055160", fontWeight: 600 }}
                          />
                        ) : (
                          SERVICE_ORIGIN_LABELS.own
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--xl"
                        sx={{ flex: "0 0 110px", textAlign: "left" }}
                        data-label="Qtd. pacote"
                      >
                        {item.packageAmount ?? "-"}
                        {(item.packageAmount ?? 1) > 1 && (
                          <Box component="span" sx={{ display: "block", fontSize: "0.72rem", color: "#6c757d" }}>
                            {item.packageChargeMode === "attended" ? "só realizadas" : "pacote inteiro"}
                          </Box>
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--md"
                        sx={{ flex: "0 0 120px", textAlign: "left" }}
                        data-label="Atendimento"
                      >
                        {item.attendanceKind === "punctual" ? (
                          <Chip
                            size="small"
                            label="Pontual"
                            sx={{ bgcolor: "#fff3cd", color: "#664d03", fontWeight: 600 }}
                          />
                        ) : (
                          "Continuado"
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--lg"
                        sx={{ flex: "0 0 130px", textAlign: "left" }}
                        data-label="Atendimento"
                      >
                        {item.chargePerSession === false ? (
                          <Chip
                            size="small"
                            label="Fora do volume"
                            title="Não conta como atendimento: fora do volume e dos indicadores; exibido à parte"
                            sx={{ bgcolor: "#e2e3e5", color: "#41464b", fontWeight: 600 }}
                          />
                        ) : (
                          "Conta"
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell c4u-col--lg"
                        sx={{ flex: "0 0 130px", textAlign: "left" }}
                        data-label="Visível agenda"
                      >
                        {item.visibleOnSchedule ? "Sim" : "Não"}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 100px", textAlign: "left" }}
                        data-label="Status"
                      >
                        <Chip
                          label={item.isActive ? "Ativo" : "Inativo"}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(item);
                          }}
                          title={
                            item.isActive
                              ? "Clique para inativar"
                              : "Clique para ativar"
                          }
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            cursor: "pointer",
                            backgroundColor: item.isActive
                              ? "#28a745"
                              : "#adb5bd",
                          }}
                        />
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 130px", textAlign: "right" }}
                        data-label="Ações"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          {canServiceValues && item.professionalType && (
                            <IconButton
                              className="c4u-row-action"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateTo("service-professional-values", {
                                  professionalTypeId:
                                    item.professionalType || "",
                                  serviceTypeId: item.id,
                                });
                              }}
                              title="Valores por profissional"
                              sx={{
                                backgroundColor: "#fd7e14",
                                color: "white",
                                "&:hover": { backgroundColor: "#e8590c" },
                              }}
                            >
                              <Paid sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          )}
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(item);
                            }}
                            title="Editar"
                            sx={{
                              backgroundColor: "#03B4C6",
                              color: "white",
                              "&:hover": { backgroundColor: "#029AAB" },
                            }}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <Tooltip
                            title={
                              item.inUse
                                ? "Não é possível excluir: serviço vinculado a valores de profissional"
                                : "Excluir"
                            }
                          >
                            <span>
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                disabled={item.inUse}
                                onClick={() => setToDelete(item)}
                                sx={{
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#c82333" },
                                  "&.Mui-disabled": {
                                    backgroundColor: "#e9a3aa",
                                    color: "white",
                                  },
                                }}
                              >
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>

            {totalCount > 0 && (
              <StandardPagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalCount}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => {
                  setItemsPerPage(n);
                  setCurrentPage(1);
                }}
              />
            )}
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <ServiceTypeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entityId={entityId}
        professionalTypes={professionalTypes}
        serviceType={editing}
        onSaved={handleSaved}
      />

      <ImportSheetModal
        open={importOpen}
        title="Importar procedimentos do convênio"
        hint="A operadora entrega a lista do que a clínica pode faturar por especialidade. Baixe o modelo, cole os códigos e suba: cada linha vira um serviço de origem Convênio."
        templateLabel="Baixar modelo"
        onDownloadTemplate={async () => {
          const blob = await serviceTypeService.importTemplate();
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "modelo-procedimentos-convenio.xlsx";
          link.click();
          URL.revokeObjectURL(url);
        }}
        onSend={(file, dryRun) => serviceTypeService.importSheet(file, dryRun)}
        onClose={() => setImportOpen(false)}
        onDone={(r) => {
          showToast(`${r.created} serviço(s) incluído(s), ${r.updated} atualizado(s).`, "success");
          loadServiceTypes();
        }}
      />

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir Serviço"
        message={`Deseja realmente excluir o serviço "${
          toDelete?.description || ""
        }"?`}
        confirmButtonText="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
        type="danger"
        isProcessing={deleting}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntityServiceTypes;
